import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Typography from '../../../primitives/typography';
import { breakpoints } from '../../../primitives/tokens';
import Image from '../../../primitives/image';
import { colors } from '../../../primitives/colors';
import Link from '../../../primitives/link';

type ServiceType = unknown & {
    serviceLine: string
    sort: number
};
interface ServiceTypesIconsProps {
    small?: boolean
    permalink: string
    servicesProvidedReadable: ServiceType[]
}

const ServiceTypesIcons = ({ servicesProvidedReadable, permalink = '', small }: ServiceTypesIconsProps): JSX.Element | null => {
    const {
        allCsServiceTypes: {
            nodes: allServiceTypes,
        },
    } = useStaticQuery<Queries.AllServiceTypesIconsQuery>(graphql`
        query AllServiceTypesIcons {
            allCsServiceTypes {
                nodes {
                    title
                    sort
                    icon {
                        title
                        url
                    }
                    franchiseLink {
                        href
                    }
                    link {
                        title
                        href
                    }
                    franchiseServiceMapping
                }
            }
        }
    `);

    if (!servicesProvidedReadable || servicesProvidedReadable.length === 0) {
        return null;
    }

    const formattedServiceTypes = allServiceTypes
        .filter((x) => {
            const splitMap = (x.franchiseServiceMapping || '').split(',');
            return servicesProvidedReadable.find(y => splitMap.includes(y.serviceLine)) && x.icon?.url;
        })
        .sort((a, b) => (a.sort || 0) - (b.sort || 0))
        .map(filteredService => ({
            ...filteredService,
            link: {
                ...filteredService.link,
                href: permalink + (filteredService.franchiseLink?.href || '#'),
            },
        }));

    return (
        <ul css={{
            display: 'grid',
            width: '100%',
            gap: small ? '4px' : '12px',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            height: small ? 'fit-content' : 'unset',
            [breakpoints.tb]: {
                display: small ? 'flex' : 'grid',
                gridTemplateColumns: small ? 'unset' : 'repeat(2, minmax(0, 1fr))',
            },
        }}
        >
            {formattedServiceTypes.map((data) => {
                if (!data) {
                    return null;
                }

                return (
                    <li key={data.franchiseServiceMapping}>
                        <Link
                            css={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'start',
                                alignItems: 'start',
                                width: 'fit-content',
                                transition: 'all .15s ease-in',
                                '&.hover-default': {
                                    transition: 'all .15s ease-in',
                                },
                                '&:hover': {
                                    transform: 'scale(1.08)',
                                    color: colors.shadesBlack,
                                    opacity: 1,
                                },
                                '& .service-type-title::after': {
                                    cursor: 'default',
                                    content: '""',
                                    display: 'block',
                                    height: '2px',
                                    width: '0',
                                    margin: '0 auto',
                                    transition: 'all .1s linear',
                                },
                                '&:hover .service-type-title::after': {
                                    transition: 'all .25s ease-in',
                                    maxWidth: '100px',
                                    width: '80%',
                                    borderRadius: '1px',
                                    margin: '0 auto',
                                    backgroundColor: colors.primaryOrange,
                                },
                            }}
                            to={data.link.href}
                        >
                            <div css={{
                                paddingBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'start',
                            }}
                            >
                                {!!data.icon?.url && (
                                    <div css={{
                                        position: 'relative',
                                        flexShrink: '1',
                                        display: 'flex',
                                    }}
                                    >
                                        <Image
                                            css={{
                                                flex: 'none',
                                                width: small ? '28px' : '48px',
                                                height: '100%',
                                                [breakpoints.tb]: {
                                                    width: small ? '36px' : '32px',
                                                },
                                            }}
                                            image={data.icon}
                                            alt={data.icon.title || ''}
                                        />
                                    </div>
                                )}
                                <Typography
                                    fontSize={{ dt: small ? 'base' : 'xl', tb: 'lg' }}
                                    fontWeight="medium"
                                    css={{
                                        marginLeft: '8px',
                                        [breakpoints.tb]: {
                                            marginLeft: '4px',
                                            display: small ? 'none' : 'initial',
                                        },
                                    }}
                                    className="service-type-title"
                                >
                                    {data.title}
                                </Typography>
                            </div>
                        </Link>
                    </li>
                );
            })}
        </ul>
    );
};

export default ServiceTypesIcons;
